import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { CalendarClock } from "lucide-react";

const HUGH = [
  { l: "H", t: "HIGHLIGHT", d: "We identify what is already valuable, credible, and worth emphasizing." },
  { l: "U", t: "UPGRADE", d: "We strengthen the areas weakening trust, positioning, user experience, and conversion." },
  { l: "G", t: "GENERATE", d: "We build better pathways for generating qualified inquiries and opportunities." },
  { l: "H", t: "HARVEST", d: "We help turn stronger positioning and acquisition infrastructure into growth, repeat business, referrals, and long-term value." },
];

const Section = ({ title, children, testid }) => (
  <div className="rounded-2xl border border-[#1c1f25] bg-[#101216] p-6" data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C6FF00]">{title}</p>
    <div className="mt-3 text-[15px] leading-relaxed text-[#c8cdd3]">{children}</div>
  </div>
);

export default function AuditResults() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/acquisition/results/${token}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [token]);

  if (err) return <div className="flex min-h-screen items-center justify-center bg-[#0A0C0F] px-6 text-center text-white"><div><h1 className="text-2xl font-extrabold">Results not available yet</h1><p className="mt-3 text-[#8A939B]">Your audit is still being reviewed by a strategist. You'll receive an email the moment it's ready.</p></div></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-[#0A0C0F] text-[#8A939B]">Loading your results…</div>;

  const a = data.audit || {};
  return (
    <div className="min-h-screen bg-[#0A0C0F] text-white">
      <Seo title="Your HughLaunch Brand Score™" description="Your strategist-reviewed HughLaunch Brand Score and audit findings." path={`/audit/results/${token}`} noindex />
      <div className="mx-auto max-w-3xl space-y-6 px-5 py-14">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C6FF00]">HughLaunch Brand Score™ — {data.business_name}</p>
          <div className="mt-6 inline-flex h-40 w-40 items-center justify-center rounded-full border-4 border-[#C6FF00]">
            <div><span className="text-5xl font-extrabold" data-testid="results-score">{data.score}</span><span className="text-xl text-[#8A939B]">/100</span></div>
          </div>
          <p className="mt-4 text-lg font-extrabold tracking-wide text-[#C6FF00]" data-testid="results-classification">{data.classification}</p>
          <p className="mx-auto mt-3 max-w-xl text-sm text-[#8A939B]">A measure of how effectively your current brand and acquisition system communicate value, build trust, and guide potential clients toward action.</p>
        </div>

        <div className="space-y-3">
          {data.categories.map((c) => (
            <div key={c.key} className="rounded-2xl border border-[#1c1f25] bg-[#101216] p-4" data-testid={`results-cat-${c.key}`}>
              <div className="flex items-center justify-between text-sm font-bold"><span>{c.label}</span><span className="text-[#C6FF00]">{c.score}/100</span></div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#1c1f25]"><div className="h-full rounded-full bg-[#C6FF00]" style={{ width: `${c.score}%` }} /></div>
            </div>
          ))}
        </div>

        <Section title="What Is Already Working" testid="results-working"><p>{a.whats_working}</p><p className="mt-2 text-sm text-[#8A939B]">Strongest area: <span className="font-bold text-white">{a.strongest_area}</span></p></Section>
        <Section title="What We Found" testid="results-found"><p>{a.current_position}</p></Section>
        <Section title="Your 3 Biggest Growth Leaks" testid="results-leaks">
          <ol className="space-y-3">{(a.growth_leaks || []).map((g, i) => <li key={i} className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#C6FF00] text-sm font-extrabold text-[#0A0C0F]">{i + 1}</span><span>{g}</span></li>)}</ol>
        </Section>
        <Section title="Your Biggest Opportunity" testid="results-opportunity"><p>{a.biggest_opportunity}</p></Section>
        <Section title="What Should Be Addressed First" testid="results-first"><p>{a.address_first}</p></Section>

        {data.call?.start_display && (
          <div className="rounded-2xl border border-[#C6FF00]/40 bg-[#C6FF00]/[0.06] p-6" data-testid="results-call">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C6FF00]">Your Audit Review Call</p>
            <p className="mt-3 flex items-center gap-2 text-lg font-extrabold"><CalendarClock className="h-5 w-5 text-[#C6FF00]" /> {data.call.start_display}</p>
            <p className="mt-2 text-sm text-[#8A939B]">We'll walk through your score, your growth leaks, and exactly what should be addressed first — together.</p>
          </div>
        )}

        <div className="rounded-2xl border border-[#1c1f25] bg-[#101216] p-6">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C6FF00]">How We Close the Gap — The H.U.G.H. Method™</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {HUGH.map((h, i) => (
              <div key={i} className="rounded-xl border border-[#23262c] bg-[#14161b] p-4">
                <p className="font-extrabold"><span className="text-[#C6FF00]">{h.l}</span> — {h.t}</p>
                <p className="mt-1 text-sm text-[#8A939B]">{h.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
